/**
 * Single source of truth for client-side auth tokens.
 *
 * Reads/writes localStorage keys:
 *   - authToken         : local-login JWT access token
 *   - azureAuthToken    : Azure SSO access token (still our own backend JWT)
 *   - refreshToken      : refresh token (shared by both auth flows)
 *
 * Centralised so the axios interceptor and AuthContext stay in sync.
 */

export const ACCESS_TOKEN_KEY = 'authToken';
export const AZURE_TOKEN_KEY = 'azureAuthToken';
export const REFRESH_TOKEN_KEY = 'refreshToken';

export type AccessTokenKind = 'jwt' | 'azure_sso';

export const tokenStorage = {
  getAccessToken(): string | null {
    return (
      localStorage.getItem(AZURE_TOKEN_KEY) ||
      localStorage.getItem(ACCESS_TOKEN_KEY)
    );
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setAccessToken(token: string, kind: AccessTokenKind): void {
    if (kind === 'azure_sso') {
      localStorage.setItem(AZURE_TOKEN_KEY, token);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    } else {
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
      localStorage.removeItem(AZURE_TOKEN_KEY);
    }
  },

  /**
   * Replace whichever access-token slot is currently in use. Used by the
   * /refresh flow which doesn't know (and shouldn't care) which login
   * method was used originally.
   */
  updateAccessToken(token: string): void {
    if (localStorage.getItem(AZURE_TOKEN_KEY)) {
      localStorage.setItem(AZURE_TOKEN_KEY, token);
    } else {
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
    }
  },

  setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  },

  clearAll(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(AZURE_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};
